// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { Trash2, Download, RefreshCw, ListChecks } from 'lucide-react';
import { formatFileSize, formatDateTime } from '../../utils/files';

const TYPE_STYLES = {
  CSV: 'bg-green-100 text-green-700',
  PDF: 'bg-red-100 text-red-700',
  XLS: 'bg-emerald-100 text-emerald-700',
};

const STATUS_STYLES = {
  PARSED: 'text-blue-700',
  COMMITTED: 'text-green-700',
  FAILED: 'text-red-600',
};

// files: FeedFile list from GET /api/feed/files/ (keyed by id)
export function FileListTable({
  files,
  loading,
  message,
  selectedFiles,
  onSelectAll,
  onToggleSelect,
  onReview,
  onDelete,
  onDownload,
  onRefresh,
  onBulkDelete,
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 gap-2 text-gray-500">
        <span className="h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <span className="text-sm">Loading files…</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500">
          {files.length} file{files.length !== 1 ? 's' : ''}
          {selectedFiles.size > 0 && ` · ${selectedFiles.size} selected`}
        </span>
        <div className="flex gap-2">
          <button
            onClick={onRefresh}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
          {selectedFiles.size > 0 && (
            <button
              onClick={onBulkDelete}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              <Trash2 size={14} />
              Delete ({selectedFiles.size})
            </button>
          )}
        </div>
      </div>

      {/* Message */}
      {message && (
        <p
          className={`text-sm px-3 py-2 rounded-lg border ${
            message.includes('Error')
              ? 'bg-red-50 text-red-700 border-red-200'
              : 'bg-green-50 text-green-700 border-green-200'
          }`}
        >
          {message}
        </p>
      )}

      {/* Empty state */}
      {files.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p className="text-sm">No files uploaded yet.</p>
          <p className="text-xs mt-1">Switch to Upload to add files.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="py-3 px-3 w-8">
                  <input
                    type="checkbox"
                    aria-label="Select all files"
                    checked={selectedFiles.size === files.length && files.length > 0}
                    onChange={onSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600"
                  />
                </th>
                {['File', 'Type', 'Bank', 'Status', 'Rows', 'Size', 'Uploaded', 'Actions'].map((h) => (
                  <th key={h} className="py-3 px-3 text-left text-xs font-semibold text-gray-600">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {files.map((f) => (
                <tr key={f.id} className="hover:bg-gray-50 transition-colors">
                  <td className="py-3 px-3">
                    <input
                      type="checkbox"
                      aria-label={`Select ${f.file_name}`}
                      checked={selectedFiles.has(f.id)}
                      onChange={(e) => onToggleSelect(f.id, e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600"
                    />
                  </td>
                  <td className="py-3 px-3">
                    <span className="font-medium text-gray-800 truncate max-w-xs block" title={f.file_name}>
                      {f.file_name}
                    </span>
                    {f.error_message && (
                      <span className="block text-xs text-red-600">{f.error_message}</span>
                    )}
                  </td>
                  <td className="py-3 px-3">
                    <span
                      className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${TYPE_STYLES[f.file_type] || ''}`}
                    >
                      {f.file_type}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-gray-600">{f.bank_name}</td>
                  <td className={`py-3 px-3 text-xs font-medium ${STATUS_STYLES[f.status] || 'text-gray-600'}`}>
                    {f.status_display || f.status}
                  </td>
                  <td className="py-3 px-3 text-gray-600 whitespace-nowrap">
                    {f.committed_rows}/{f.total_rows} committed
                    {f.pending_rows > 0 && ` · ${f.pending_rows} pending`}
                  </td>
                  <td className="py-3 px-3 text-gray-500">{f.has_file ? formatFileSize(f.file_size) : '—'}</td>
                  <td className="py-3 px-3 text-gray-500">{formatDateTime(f.uploaded_at)}</td>
                  <td className="py-3 px-3">
                    <div className="flex gap-1">
                      <button
                        onClick={() => onReview(f)}
                        className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-700"
                        title="Review rows"
                        aria-label={`Review ${f.file_name}`}
                      >
                        <ListChecks size={14} />
                      </button>
                      <button
                        onClick={() => onDownload(f)}
                        disabled={!f.has_file}
                        className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        title={f.has_file ? 'Download original' : 'Original not stored'}
                        aria-label={`Download ${f.file_name}`}
                      >
                        <Download size={14} />
                      </button>
                      <button
                        onClick={() => onDelete(f)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"
                        title="Delete"
                        aria-label={`Delete ${f.file_name}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
